import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabase";

const LoginPage = () => {
  const navigate = useNavigate();

  // M1 login state (staff from Supabase)
  const [m1Code, setM1Code] = useState("");
  const [m1Password, setM1Password] = useState("");
  const [m1Error, setM1Error] = useState("");
  const [m1ShowPwd, setM1ShowPwd] = useState(false);

  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const handleM1Login = async (e: React.FormEvent) => {
    e.preventDefault();
    setM1Error("");
    setLoading(true);

    try {
      // Query staff table from Supabase
      const { data: staffList, error } = await supabase
        .from("staff")
        .select("*")
        .eq("m_code", m1Code.trim().toUpperCase())
        .maybeSingle();

      if (error) {
        setM1Error("Lỗi kết nối hệ thống. Vui lòng thử lại.");
        setLoading(false);
        return;
      }

      if (!staffList) {
        setM1Error("Mã code không tồn tại. Vui lòng kiểm tra lại.");
        setLoading(false);
        return;
      }

      if (staffList.status === "inactive") {
        setM1Error("Tài khoản này hiện đang bị vô hiệu hóa. Liên hệ quản trị viên.");
        setLoading(false);
        return;
      }

      if (staffList.password !== m1Password) {
        setM1Error("Mật khẩu không đúng. Vui lòng kiểm tra lại.");
        setLoading(false);
        return;
      }

      // Login success
      const isSuperAdmin = staffList.m_code === "M1-11-000";
      localStorage.setItem("bizwell_auth", JSON.stringify({
        type: "m1",
        user: {
          id: staffList.id,
          code: staffList.m_code,
          name: staffList.full_name,
          role: isSuperAdmin ? "super_admin" : "staff",
          status: staffList.status,
        }
      }));

      setSuccessMsg(`Xin chào, ${staffList.full_name}! Đang chuyển hướng...`);
      setTimeout(() => navigate("/admin"), 1200);
    } catch {
      setM1Error("Lỗi hệ thống. Vui lòng thử lại.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F9FA] via-white to-[#EDF2FB] flex flex-col">
      {/* Top bar */}
      <div className="bg-[#004A99] text-white text-xs py-2 px-6 text-center">
        <Link to="/" className="hover:underline cursor-pointer">
          ← Quay lại trang chủ Bizwell
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-3 cursor-pointer">
              <div className="w-12 h-12 rounded-xl bg-[#004A99] flex items-center justify-center">
                <span className="text-white font-black text-lg">BW</span>
              </div>
              <div className="text-left">
                <div className="text-[#004A99] font-black text-xl tracking-tight">Bizwell</div>
                <div className="text-[9px] text-gray-400 font-medium tracking-wide">Nền Tảng B2B Công Nghệ Hàn–Việt</div>
              </div>
            </Link>
            <p className="mt-4 text-sm text-gray-500">
              Đăng nhập để truy cập hệ thống quản trị
            </p>
          </div>

          {/* Card */}
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="bg-[#004A99] px-6 py-4 text-center">
              <div className="flex items-center justify-center gap-2 text-white">
                <i className="ri-shield-user-line text-xl" />
                <span className="font-bold text-base">Đăng Nhập Nội Bộ</span>
              </div>
              <p className="text-white/70 text-xs mt-1">Dành cho nhân viên Bizwell (M-Code)</p>
            </div>

            <div className="p-7">
              {successMsg && (
                <div className="mb-5 flex items-center gap-2.5 bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm">
                  <i className="ri-checkbox-circle-line text-base" />
                  {successMsg}
                </div>
              )}

              <form onSubmit={handleM1Login} className="space-y-5">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-800 leading-relaxed">
                  <i className="ri-shield-keyhole-line mr-1" />
                  <strong>Dành cho nhân viên nội bộ Bizwell.</strong> Liên hệ quản trị viên nếu quên mã code.
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Mã nhân viên (M-Code) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                      <i className="ri-barcode-line text-sm text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={m1Code}
                      onChange={(e) => setM1Code(e.target.value)}
                      placeholder="M1-11-000 hoặc M1-12-001"
                      required
                      className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:border-[#004A99] focus:ring-1 focus:ring-[#004A99]/30 outline-none transition-all font-mono"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Mật khẩu <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                      <i className="ri-lock-line text-sm text-gray-400" />
                    </div>
                    <input
                      type={m1ShowPwd ? "text" : "password"}
                      value={m1Password}
                      onChange={(e) => setM1Password(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="w-full pl-9 pr-10 py-2.5 text-sm border border-gray-200 rounded-lg focus:border-[#004A99] focus:ring-1 focus:ring-[#004A99]/30 outline-none transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setM1ShowPwd(!m1ShowPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <i className={m1ShowPwd ? "ri-eye-off-line text-sm" : "ri-eye-line text-sm"} />
                    </button>
                  </div>
                </div>

                {m1Error && (
                  <div className="flex items-center gap-2 text-red-600 text-xs bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
                    <i className="ri-error-warning-line text-sm" />
                    {m1Error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#004A99] hover:bg-[#003d7d] text-white py-3 rounded-xl font-semibold text-sm transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <><i className="ri-loader-4-line animate-spin" /> Đang xác thực...</>
                  ) : (
                    <><i className="ri-shield-check-line" /> Đăng Nhập</>
                  )}
                </button>

                <div className="text-center text-xs text-gray-400 border-t pt-4">
                  <p>Mật khẩu mặc định: <code className="bg-gray-100 px-1 rounded font-mono">[SĐT] + [3 ký tự cuối tên]</code></p>
                </div>
              </form>
            </div>
          </div>

          {/* Footer note */}
          <p className="text-center text-xs text-gray-400 mt-6">
            © 2025 Bizwell · 탄소나노협회 · Nền tảng B2B Hàn–Việt
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
